import { useParams } from 'react-router-dom';
import ContentLayout from '@layouts/ContentLayout';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Spinner,
  StackDivider,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import ModalLayout from '@components/Modal/ModalLayout';
import { useDispatch } from 'react-redux';
import { MdEdit } from 'react-icons/md';
import DynamicInputModal from '@components/DynamicInputModal';
import { beneficiaryConfig as config } from './utils';
import { BENEFICIARY_STRING_CONSTANTS } from './constant';
import CopyableText from '@components/CopyableText';
import usePromise from '@hooks/usePromise';


const UserBeneficiaries = ({}) => {
  const dispatch = useDispatch();
  const { userId } = useParams();
  const {
    isLoading: isBeneficiariesLoading,
    run,
    error: beneficiariesError,
    data: beneficiariesData
  } = usePromise();
  const [beneficiaryList, setBeneficiaryList] = useState(null);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    isOpen: isUpdateBeneficiaryModalOpen,
    onOpen: onUpdateBeneficiaryModalOpen,
    onClose: onUpdateBeneficiaryModalClose
  } = useDisclosure();

  useEffect(() => {
    triggerGetBeneficiaryList(userId);
  }, [userId]);

  useEffect(() => {
    if(beneficiariesData) {
      setBeneficiaryList(beneficiariesData)
    }
  }, [beneficiariesData]);

  const triggerGetBeneficiaryList = useCallback((userId) => {
    try {
      const resPromise = dispatch(
        config.getUserBeneficiaries({
          userId
        })
      );
      run(resPromise);
    } catch (error) {
      console.error("Error :", error);
    }
  }, [userId, dispatch, run]);

  const handleUpdateBeneficiaryModalOpen = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    onUpdateBeneficiaryModalOpen();
  };

  const handleOnSuccessUpdateCleanUp = useCallback(() => {
    setSelectedBeneficiary(null);
    onUpdateBeneficiaryModalClose();
  }, []);

  const handleUpdateBeneficiaryModalSubmit = (formData) => {
    dispatch(
      config.updateBeneficiaryDetails({
        userId,
        beneficiaryId: selectedBeneficiary.beneficiaryId,
        payload: config.updateBeneficiaryDetailsPayloadConverter({
          beneficiaryData: selectedBeneficiary,
          formData
        }),
        onSuccess: () => {
          handleOnSuccessUpdateCleanUp()
          triggerGetBeneficiaryList(userId);
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    )
  }

  return (
    <Box
      position={'relative'}
      overflowX={'hidden'}
      overflowY={'auto'}
      flex={'1'}
    >
      <ModalLayout
        isOpen={isUpdateBeneficiaryModalOpen}
        onClose={onUpdateBeneficiaryModalClose}
      >
        <DynamicInputModal
          title={"Update Beneficiary Details"}
          isOpen={isUpdateBeneficiaryModalOpen}
          onClose={onUpdateBeneficiaryModalClose}
          onSubmit={handleUpdateBeneficiaryModalSubmit}
          fields={config.updateBeneficiaryFieldsConfig(selectedBeneficiary) || []}
          isLoading={isModalLoading}
        />
      </ModalLayout>
      <ContentLayout>
        <Grid gridTemplateColumns={{ base: '1fr', lg: `1fr 0fr` }}>
          <VStack
            alignItems="stretch"
            gap={4}
            px={4}
            py={8}
            divider={<StackDivider borderColor="whiteAlpha.300" />}
          >
            <HStack justify={'space-between'}>
              <Heading size={"md"}> {config.title} </Heading>
            </HStack>
            {isBeneficiariesLoading ? (
              <Flex justifyContent="center" alignItems="center" w="full" h="full">
                <Spinner thickness="4px" size="lg" color="colorPrimary" />
              </Flex>
            ) : beneficiariesError ? (
              <Box p={4} borderRadius="xl" bg={'backGround'} borderWidth="1px" borderColor={'whiteAlpha.300'}>
                <Text color="white" fontSize="md">
                  {beneficiariesError}
                </Text>
              </Box>
            ) : beneficiaryList?.length ? (
              <Box
                position={'relative'}
                gap={4}
              >
                <Heading
                  fontSize={'lg'}
                  color={'colorPrimary'}
                >
                  Beneficiary Table
                </Heading>
                <TableContainer
                  border="2px solid"
                  borderColor="whiteAlpha.500"
                  borderRadius="xl"
                  my={4}
                  overflowX="auto"
                >
                  <Table variant="unstyled">
                    <Thead>
                      <Tr
                        bg="backGround"
                        borderBottom="2px solid"
                        borderColor="whiteAlpha.500"
                      >
                        {BENEFICIARY_STRING_CONSTANTS.BENEFICIARY_TABLE_COLUMNS.map((header) => (
                          <Th key={header} textAlign="center" whiteSpace="nowrap">
                            <Text fontSize="xs">{header}</Text>
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {beneficiaryList?.map((beneficiary, id) => (
                        <Tr key={beneficiary?.beneficiaryId}>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <Text fontSize="xs" color="white">
                              {id + 1 || "-"}
                            </Text>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <CopyableText fontSize="xs" color="white">
                              {beneficiary?.accountNumber || "-"}
                            </CopyableText>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <CopyableText fontSize="xs" color="white">
                              {beneficiary?.ifscCode || "-"}
                            </CopyableText>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <CopyableText fontSize="xs" color="white">
                              {beneficiary?.accountHolderName || "-"}
                            </CopyableText>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <Text fontSize="xs" color="white">
                              {beneficiary?.accountState || "-"}
                            </Text>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <Text fontSize="xs" color="white">
                              {beneficiary?.bankName || "-"}
                            </Text>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <CopyableText fontSize="xs" color="white">
                              {beneficiary?.beneficiaryId || "-"}
                            </CopyableText>
                          </Td>
                          <Td textAlign="center" whiteSpace="nowrap">
                            <Button
                              colorScheme={'brand'}
                              size="xs"
                              bg="black"
                              border={'1px solid'}
                              borderColor={'whiteAlpha.300'}
                              borderRadius={'lg'}
                              p={0}
                              onClick={() => handleUpdateBeneficiaryModalOpen(beneficiary)}
                            >
                              <MdEdit />
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box p={4} borderRadius="xl" bg={'backGround'} borderWidth="1px" borderColor={'whiteAlpha.300'}>
                <Text color="white" fontSize="md">
                  {BENEFICIARY_STRING_CONSTANTS.NO_BENEFICIARY_FOUND}
                </Text>
              </Box>
            )}
          </VStack>
        </Grid>
      </ContentLayout>
    </Box>
  );
};

export default UserBeneficiaries;